import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { extractApiErrorMessage } from "@/lib/api-error";
import { errorTitle } from "@/lib/messages";

export interface Ingredient {
  id: string;
  storeId: string;
  name: string;
  isActive: boolean;
  stockQuantity: number | null;
  createdAt: string;
  updatedAt: string;
}

export const INGREDIENTS_QUERY_KEY = ["ingredients"];

export function listIngredients(): Promise<Ingredient[]> {
  return customFetch<Ingredient[]>("/api/ingredients", { method: "GET" });
}

export function useIngredients() {
  return useQuery({ queryKey: INGREDIENTS_QUERY_KEY, queryFn: listIngredients });
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function IngredientsManagerDialog({ open, onClose }: Props): JSX.Element {
  const { data: ingredients = [], isLoading } = useIngredients();
  const [newName, setNewName] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      customFetch<Ingredient>("/api/ingredients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, isActive: true, stockQuantity: null }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INGREDIENTS_QUERY_KEY });
      setNewName("");
    },
    onError: (err: any) => {
      toast({
        title: errorTitle("criar o ingrediente"),
        description: extractApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pick<Ingredient, "name" | "isActive" | "stockQuantity">> }) =>
      customFetch<Ingredient>(`/api/ingredients/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INGREDIENTS_QUERY_KEY });
    },
    onError: (err: any) => {
      toast({
        title: errorTitle("atualizar o ingrediente"),
        description: extractApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      customFetch<{ success: boolean }>(`/api/ingredients/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INGREDIENTS_QUERY_KEY });
    },
    onError: (err: any) => {
      toast({
        title: errorTitle("excluir o ingrediente"),
        description: extractApiErrorMessage(err),
        variant: "destructive",
      });
    },
  });

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate(name);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg">
        <DialogHeader>
          <DialogTitle>Ingredientes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <p className="text-xs text-muted-foreground">
            Desative ou ajuste o estoque. Mudanças aqui valem pra <strong>todos os produtos</strong> que usam esse ingrediente.
          </p>

          <div className="flex gap-2">
            <Input
              placeholder="Nome do novo ingrediente"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreate(); } }}
              className="flex-1 min-w-0 h-9 text-sm"
            />
            <Button type="button" className="shrink-0" onClick={handleCreate} disabled={!newName.trim() || createMutation.isPending}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
            </Button>
          </div>

          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {isLoading ? (
              <p className="text-xs text-muted-foreground py-4 text-center">Carregando…</p>
            ) : ingredients.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">Nenhum ingrediente cadastrado ainda.</p>
            ) : (
              ingredients.map((ing) => (
                <IngredientRow
                  key={ing.id}
                  ingredient={ing}
                  onPatch={(patch) => updateMutation.mutate({ id: ing.id, patch })}
                  onDelete={async () => {
                    if (await confirm({
                      title: `Excluir "${ing.name}"?`,
                      description: "Produtos que usam esse ingrediente vão perder essa opção.",
                      confirmLabel: "Excluir",
                    })) {
                      deleteMutation.mutate(ing.id);
                    }
                  }}
                />
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IngredientRow({ ingredient, onPatch, onDelete }: {
  ingredient: Ingredient;
  onPatch: (patch: Partial<Pick<Ingredient, "name" | "isActive" | "stockQuantity">>) => void;
  onDelete: () => void;
}): JSX.Element {
  const [name, setName] = useState(ingredient.name);
  const [stockEnabled, setStockEnabled] = useState(ingredient.stockQuantity !== null);
  const [stockStr, setStockStr] = useState(ingredient.stockQuantity != null ? String(ingredient.stockQuantity) : "");

  // Sincroniza estado local com mudanças do servidor (ex: outra aba editou).
  useEffect(() => {
    setName(ingredient.name);
    setStockEnabled(ingredient.stockQuantity !== null);
    setStockStr(ingredient.stockQuantity != null ? String(ingredient.stockQuantity) : "");
  }, [ingredient.name, ingredient.stockQuantity]);

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === ingredient.name) return;
    onPatch({ name: trimmed });
  };

  const commitStock = () => {
    const target = stockEnabled ? (() => {
      const n = parseInt(stockStr, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    })() : null;
    if (target === ingredient.stockQuantity) return;
    onPatch({ stockQuantity: target });
  };

  return (
    <div className="border border-border/50 rounded-lg p-3 space-y-2 bg-muted/10">
      <div className="flex items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          className="flex-1 min-w-0 h-8 text-sm bg-card"
        />
        <Switch
          checked={ingredient.isActive}
          onCheckedChange={(v) => onPatch({ isActive: v })}
          title={ingredient.isActive ? "Ativo (clique pra desativar)" : "Inativo (clique pra ativar)"}
        />
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={onDelete}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
          <input
            type="checkbox"
            checked={stockEnabled}
            onChange={(e) => {
              const next = e.target.checked;
              setStockEnabled(next);
              if (!next) {
                // Desabilitou controle: salva null direto.
                onPatch({ stockQuantity: null });
              }
            }}
            className="accent-primary w-3.5 h-3.5"
          />
          Controlar estoque
        </label>
        {stockEnabled && (
          <>
            <Input
              type="number"
              min="0"
              value={stockStr}
              onChange={(e) => setStockStr(e.target.value)}
              onBlur={commitStock}
              className="w-24 h-8 text-sm"
              placeholder="Qtd"
            />
            {ingredient.stockQuantity != null && ingredient.stockQuantity === 0 && (
              <span className="text-xs text-destructive font-medium flex items-center gap-1">
                <X className="w-3 h-3" /> Esgotado
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
